import { FormGroup } from 'shared-components';
import { Label } from 'shared-components';
import { Input } from 'shared-components';
import { Checkbox } from 'shared-components';
import styles from './settings.module.css';
import classNames from 'classnames';
import { open } from '../modal/modalSlice';
import { toast } from 'react-toastify';
import {
  useDeleteLogoMutation,
  useDeleteLogoLightMutation,
  useGetSettingsQuery,
} from './settings.api';
import { Button } from 'shared-components';
import { ButtonIntent, ButtonTier } from 'shared-components';
import { useAppDispatch } from '../../app/hooks';
import { useFormikContext } from 'formik';

const LogoPreview = ({
  logoUrl,
  label,
  onClear,
  onUpload,
}: {
  logoUrl: string | undefined | null;
  label: string;
  onClear: () => void;
  onUpload: () => void;
}) => {
  return (
    <div className={styles.logo}>
      <div
        className={classNames(styles.logo__cover, {
          [styles.logo__cover_empty]: !logoUrl,
          [styles.logo__cover_full]: logoUrl,
        })}
        style={{
          backgroundImage: logoUrl ? `url(${logoUrl})` : undefined,
        }}
      ></div>
      <div className={styles.logo__buttons}>
        {logoUrl && (
          <Button
            onClick={onClear}
            intent={ButtonIntent.Negative}
            tier={ButtonTier.Secondary}
          >
            Clear
          </Button>
        )}
        <Button
          className="w-100"
          onClick={onUpload}
          intent={ButtonIntent.Positive}
          tier={ButtonTier.Secondary}
        >
          {logoUrl ? 'Change' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

export const SettingsLogo = () => {
  const { data, isLoading, isFetching } = useGetSettingsQuery();
  const [deleteLogo] = useDeleteLogoMutation();
  const [deleteLogoLight] = useDeleteLogoLightMutation();
  const dispatch = useAppDispatch();
  const { getFieldProps, setFieldValue, values } = useFormikContext<{
    separateLightDarkLogo: boolean;
    [key: string]: unknown;
  }>();
  const logoUrl = data?.logoUrl;
  const logoLightUrl = data?.logoLightUrl;
  const separateLogo = values.separateLightDarkLogo;

  return (
    <>
      <h2>Logo</h2>
      <Checkbox
        type="checkbox"
        label="Use separate light & dark logos"
        checked={separateLogo}
        onChange={(e) =>
          setFieldValue('separateLightDarkLogo', e.target.checked)
        }
      />
      {isLoading || isFetching ? (
        <span>loading...</span>
      ) : separateLogo ? (
        <div className={styles.logoColumns}>
          <div>
            <h3>Dark Mode Logo</h3>
            <LogoPreview
              logoUrl={logoUrl}
              label="Dark"
              onClear={async () => {
                if (
                  // eslint-disable-next-line no-restricted-globals
                  confirm('Are you sure you want to clear the dark mode logo?')
                ) {
                  await deleteLogo();
                  toast.success('Dark mode logo cleared');
                }
              }}
              onUpload={() => {
                dispatch(
                  open({
                    title: 'Upload dark mode logo',
                    contentKey: 'logo-upload',
                    contentPayload: { variant: 'default' },
                  })
                );
              }}
            />
          </div>
          <div>
            <h3>Light Mode Logo</h3>
            <LogoPreview
              logoUrl={logoLightUrl || logoUrl}
              label="Light"
              onClear={async () => {
                if (
                  // eslint-disable-next-line no-restricted-globals
                  confirm(
                    'Are you sure you want to clear the light mode logo?'
                  )
                ) {
                  await deleteLogoLight();
                  toast.success('Light mode logo cleared');
                }
              }}
              onUpload={() => {
                dispatch(
                  open({
                    title: 'Upload light mode logo',
                    contentKey: 'logo-upload',
                    contentPayload: { variant: 'light' },
                  })
                );
              }}
            />
          </div>
        </div>
      ) : (
        <LogoPreview
          logoUrl={logoUrl}
          label="Logo"
          onClear={async () => {
            if (
              // eslint-disable-next-line no-restricted-globals
              confirm('Are you sure you want to clear the logo?')
            ) {
              await deleteLogo();
              toast.success('Logo cleared');
            }
          }}
          onUpload={() => {
            dispatch(
              open({
                title: 'Upload logo',
                contentKey: 'logo-upload',
                contentPayload: { variant: 'default' },
              })
            );
          }}
        />
      )}
      <FormGroup>
        <Label htmlFor="logoLink">Logo Link</Label>
        <Input
          type="text"
          placeholder="https://example.com"
          {...getFieldProps('logoLink')}
        />
      </FormGroup>
    </>
  );
};
